// Small helpers. Everything here must be exception-safe: a broken widget must
// never break the host page (docs/widget.md decision §7-4).

let debugEnabled = false;
export function setDebug(on: boolean): void {
  debugEnabled = on;
}
/** the ONLY console output the widget ever produces, and only with data-debug="true" */
export function dlog(...args: unknown[]): void {
  if (debugEnabled) console.debug('[chatbi]', ...args);
}

/** localStorage/sessionStorage throw in some private modes — degrade to memory */
function makeSafeStorage(pick: () => Storage): {
  get(k: string): string | null;
  set(k: string, v: string): void;
  remove(k: string): void;
} {
  const mem = new Map<string, string>();
  return {
    get(k) {
      try {
        return pick().getItem(k);
      } catch {
        return mem.get(k) ?? null;
      }
    },
    set(k, v) {
      try {
        pick().setItem(k, v);
      } catch {
        mem.set(k, v);
      }
    },
    remove(k) {
      try {
        pick().removeItem(k);
      } catch {
        mem.delete(k);
      }
    },
  };
}

export const local = makeSafeStorage(() => window.localStorage);
export const session = makeSafeStorage(() => window.sessionStorage);

export function throttle<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    const now = Date.now();
    const wait = last + ms - now;
    if (wait <= 0) {
      last = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        timer = undefined;
        last = Date.now();
        fn(...args);
      }, wait);
    }
  };
}

export interface DeviceInfo {
  device: 'mobile' | 'desktop' | 'tablet';
  browser: string;
  os: string;
}

/** coarse UA parse — analytics breakdowns only need buckets, not precision */
export function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent;
  const iPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  const tablet = /iPad|Tablet|PlayBook|Silk/i.test(ua) || iPadOs || (/Android/.test(ua) && !/Mobile/.test(ua));
  const mobile = !tablet && /Mobi|iPhone|iPod/i.test(ua);

  let browser = 'other';
  if (/Edg\//.test(ua)) browser = 'edge';
  else if (/OPR\//.test(ua)) browser = 'opera';
  else if (/SamsungBrowser/.test(ua)) browser = 'samsung';
  else if (/Firefox\//.test(ua)) browser = 'firefox';
  else if (/Chrome\//.test(ua)) browser = 'chrome';
  else if (/Safari\//.test(ua)) browser = 'safari';

  let os = 'other';
  if (/Windows/.test(ua)) os = 'windows';
  else if (/Android/.test(ua)) os = 'android';
  else if (/iPhone|iPad|iPod/.test(ua) || iPadOs) os = 'ios';
  else if (/Macintosh/.test(ua)) os = 'macos';
  else if (/Linux/.test(ua)) os = 'linux';

  return { device: tablet ? 'tablet' : mobile ? 'mobile' : 'desktop', browser, os };
}

export function utmParams(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  try {
    const q = new URLSearchParams(window.location.search);
    return {
      utmSource: q.get('utm_source') ?? undefined,
      utmMedium: q.get('utm_medium') ?? undefined,
      utmCampaign: q.get('utm_campaign') ?? undefined,
    };
  } catch {
    return {};
  }
}

export function pageUrl(): string {
  return window.location.href.slice(0, 2000);
}

/** visitor environment snapshot for bootstrap (docs/visitor-details.md §1.1) — all best-effort */
export function envInfo(): { screen?: string; language?: string; timezone?: string } {
  const out: { screen?: string; language?: string; timezone?: string } = {};
  try {
    if (window.screen?.width) out.screen = `${window.screen.width}×${window.screen.height}`;
  } catch { /* ignore */ }
  try {
    if (navigator.language) out.language = navigator.language;
  } catch { /* ignore */ }
  try {
    out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch { /* ignore */ }
  return out;
}

/**
 * URL pattern matching — mirrors the backend survey util (url-match.util.ts) so
 * merchants get one consistent «pages» semantics. Case-insensitive. '*' is the
 * only wildcard; a pattern without '*' matches as a substring ('/checkout' hits
 * any checkout URL). Empty/absent list = every page.
 */
export function matchesAnyUrlPattern(patterns: string[] | undefined, url: string): boolean {
  if (!patterns || patterns.length === 0) return true;
  if (!url) return false;
  const target = url.toLowerCase();
  return patterns.some((pattern) => {
    const p = pattern.trim().toLowerCase();
    if (!p) return false;
    if (p.indexOf('*') === -1) return target.indexOf(p) !== -1;
    const escaped = p.split('*').map((s) => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*');
    try {
      return new RegExp(escaped).test(target);
    } catch {
      return false;
    }
  });
}

/** rough desktop check for exit-intent (needs a real pointer leaving the viewport) */
export function hasFinePointer(): boolean {
  try {
    return window.matchMedia('(pointer: fine)').matches;
  } catch {
    return !('ontouchstart' in window);
  }
}
